import type { FC, ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { breakpointsNumInPx } from 'theme/mainTheme';

export type ResponsiveContextT = {
  /** Width < 768px – mobile (tablet breakpoint) */
  isMobile: boolean;
  /** Width ≤ 425px – small mobile */
  isSmallMobile: boolean;
  /** 425px ≤ width < 768px – large mobile */
  isLargeMobile: boolean;
  /** Width ≥ 768px – tablet and up */
  isTablet: boolean;
  /** 768px ≤ width < 1280px – tablets (legacy) */
  isDesktop: boolean;
  /** Width ≥ 1440px – large desktop (legacy) */
  isLargeDesktop: boolean;
};

type ResponsiveProviderPropsT = {
  children: ReactNode;
};

const contextDefaultValue: ResponsiveContextT = {
  isMobile: false,
  isSmallMobile: false,
  isLargeMobile: false,
  isTablet: false,
  isDesktop: false,
  isLargeDesktop: false,
};

const ResponsiveContext = createContext<ResponsiveContextT>(contextDefaultValue);

const getDevicesTypes = (): ResponsiveContextT => {
  const width = window.innerWidth || 0;
  const tablet = breakpointsNumInPx.tablet ?? breakpointsNumInPx.md;
  const largeMobile = breakpointsNumInPx.largeMobile ?? 425;

  return {
    isMobile: width <= tablet,
    isSmallMobile: width <= largeMobile,
    isLargeMobile: width > largeMobile && width <= tablet,
    isTablet: width > tablet,
    isDesktop: width >= breakpointsNumInPx.xl && width < breakpointsNumInPx['2xl'],
    isLargeDesktop: width >= breakpointsNumInPx['2xl'],
  };
};

const ResponsiveProvider: FC<ResponsiveProviderPropsT> = ({ children }): JSX.Element => {
  const [devicesTypes, setDevicesTypes] = useState(getDevicesTypes());

  /**
   * Handlers
   */
  const updateDevicesTypes = (): void => {
    setDevicesTypes(getDevicesTypes());
  };

  /**
   * Effects
   */
  useEffect(() => {
    window.addEventListener('resize', updateDevicesTypes);

    return () => window.removeEventListener('resize', updateDevicesTypes);
  }, []);

  return <ResponsiveContext.Provider value={devicesTypes}>{children}</ResponsiveContext.Provider>;
};

const useResponsive = (): ResponsiveContextT => useContext(ResponsiveContext);

export { ResponsiveProvider, useResponsive };
